import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  useTheme,
  alpha,
  Chip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as RestoreIcon,
  Code as CodeIcon,
  Apps as AppsIcon,
  Add as AddIcon,
  Search as SearchIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Description as DocumentIcon,
  Description as DescriptionIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  MoreVert as MoreIcon,
  Rocket as RocketIcon,
  Settings as SettingsIcon,
  BugReport as BugIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Language as LanguageIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Favorite as FavoriteIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import UserDocumentsModal from './UserDocumentsModal';
import { CompanyService } from '../../services/companyService';
import { useNavigate } from 'react-router-dom';
import { ApiKeysService } from '../../services/apiKeysService';
import { FavoritesService } from '../../services/favoritesService';
import ReactMarkdown from 'react-markdown';
import parse from 'html-react-parser';
import { useUIStore } from '../../store/uiStore';
import LoadingSpinner from '../UI/LoadingSpinner';

interface UserDocument {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  document_type: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  url?: string;
}

interface FavorizedJob {
  id: string;
  job_id: string;
  user_id: string;
  created_at: string;
  job: {
    id: string;
    company_id: string;
    title: string;
    description: string;
    job_type: string;
    employment_type: string;
    experience_level: string;
    location: string;
    is_remote: boolean;
    salary_min?: number;
    salary_max?: number;
    salary_currency: string;
    salary_period: string;
    company: {
      id: string;
      name: string;
      logo_url?: string;
      industry?: string;
      headquarters_city?: string;
      headquarters_country?: string;
    };
  };
}

interface AIApplicationWriterModalProps {
  open: boolean;
  onClose: () => void;
  document: UserDocument | null;
  onDocumentSelect?: (document: UserDocument) => void;
  onMinimize?: () => void;
  onRestore?: () => void;
}

interface AIApplication {
  id: string;
  name: string;
  description: string;
  type: 'chatbot' | 'analyzer' | 'extractor' | 'generator';
  status: 'active' | 'draft' | 'error' | 'building';
  createdAt: string;
  lastUsed: string;
  documents: number;
  icon: React.ReactNode;
  color: string;
}

// Step definitions
const steps = [
  {
    id: 'cv-selection',
    title: 'Select CV',
    description: 'Choose your CV or upload a new one',
    icon: <DescriptionIcon />,
  },
  {
    id: 'cv-analysis',
    title: 'CV Analysis',
    description: 'Review your CV analysis results',
    icon: <CheckIcon />,
  },
  {
    id: 'job-selection',
    title: 'Job Selection',
    description: 'Choose the job you want to apply for',
    icon: <WorkIcon />,
  },
  {
    id: 'ai-generation',
    title: 'AI Generation',
    description: 'Generate AI applications for selected jobs',
    icon: <AutoAwesomeIcon />,
  },
  {
    id: 'generation',
    title: 'Done',
    description: 'All applications completed successfully',
    icon: <CheckIcon />,
  },
];

// Job categories
const jobCategories = [
  {
    id: 'software-engineer',
    title: 'Software Engineer',
    description: 'Full-stack, frontend, backend development roles',
    icon: <CodeIcon />,
    color: '#3b82f6',
  },
  {
    id: 'data-scientist',
    title: 'Data Scientist',
    description: 'Data analysis, machine learning, AI roles',
    icon: <SchoolIcon />,
    color: '#10b981',
  },
  {
    id: 'product-manager',
    title: 'Product Manager',
    description: 'Product strategy, roadmap, and management roles',
    icon: <WorkIcon />,
    color: '#f59e0b',
  },
  {
    id: 'designer',
    title: 'Designer',
    description: 'UI/UX, graphic design, and creative roles',
    icon: <AutoAwesomeIcon />,
    color: '#8b5cf6',
  },
];

// Mock data for existing job applications
const mockJobApplications: AIApplication[] = [
  {
    id: '1',
    name: 'Google Software Engineer',
    description: 'Senior Full-Stack Developer position at Google',
    type: 'chatbot',
    status: 'active',
    createdAt: '2024-01-15',
    lastUsed: '2024-01-20',
    documents: 3,
    icon: <CodeIcon />,
    color: '#3b82f6',
  },
  {
    id: '2',
    name: 'Microsoft Data Scientist',
    description: 'Machine Learning Engineer role at Microsoft',
    type: 'analyzer',
    status: 'active',
    createdAt: '2024-01-10',
    lastUsed: '2024-01-19',
    documents: 2,
    icon: <SchoolIcon />,
    color: '#10b981',
  },
  {
    id: '3',
    name: 'Apple Product Manager',
    description: 'Senior Product Manager position at Apple',
    type: 'extractor',
    status: 'draft',
    createdAt: '2024-01-18',
    lastUsed: '2024-01-18',
    documents: 1,
    icon: <WorkIcon />,
    color: '#f59e0b',
  },
  {
    id: '4',
    name: 'Meta UI/UX Designer',
    description: 'Senior Designer role at Meta',
    type: 'generator',
    status: 'building',
    createdAt: '2024-01-20',
    lastUsed: 'Never',
    documents: 0,
    icon: <AutoAwesomeIcon />,
    color: '#8b5cf6',
  },
];

const AIApplicationWriterModal: React.FC<AIApplicationWriterModalProps> = ({
  open,
  onClose,
  document,
  onDocumentSelect,
  onMinimize,
  onRestore,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { 
    jobApplicationWriterMinimized, 
    setJobApplicationWriterMinimized,
    jobApplicationWriterOpen,
    setJobApplicationWriterOpen,
    selectedJobs,
    selectedJobsCount,
    selectedJobsData,
    addSelectedJob,
    removeSelectedJob,
    setGeneratingApplication,
    isGeneratingApplication,
    setGeneratedApplication: setGeneratedApplicationStore,
    hasGeneratedApplication,
    getGeneratedApplicationId
  } = useUIStore();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCV, setSelectedCV] = useState<UserDocument | null>(null);
  const [selectedJobCategory, setSelectedJobCategory] = useState<string>('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedApp, setSelectedApp] = useState<AIApplication | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [processingCV, setProcessingCV] = useState(false);
  const [cvAnalysisResult, setCvAnalysisResult] = useState<any>(null);
  const [cvAnalysisError, setCvAnalysisError] = useState<string | null>(null);
  const [uploadingOCR, setUploadingOCR] = useState(false);
  const [showOCRChoicePopup, setShowOCRChoicePopup] = useState(false);
  const [existingOCRData, setExistingOCRData] = useState<any>(null);
  const [showApplicationPopup, setShowApplicationPopup] = useState(false);
  const [generatedApplication, setGeneratedApplicationLocal] = useState<any>(null);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [favorizedJobs, setFavorizedJobs] = useState<FavorizedJob[]>([]);
  const [loadingFavorizedJobs, setLoadingFavorizedJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState<FavorizedJob | null>(null);
  const [selectedJobDetails, setSelectedJobDetails] = useState<any[]>([]);
  const [loadingJobDetails, setLoadingJobDetails] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [loadingApplication, setLoadingApplication] = useState(false);
  const [showReplaceConfirmModal, setShowReplaceConfirmModal] = useState(false);
  const [jobToReplace, setJobToReplace] = useState<any>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Generation step state
  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedApplications, setGeneratedApplications] = useState<any[]>([]);
  const [currentJobDetails, setCurrentJobDetails] = useState<any>(null);
  const [isLoadingJobDetails, setIsLoadingJobDetails] = useState(false);

  // Local storage key for progress persistence
  const PROGRESS_STORAGE_KEY = 'jobApplicationWriterProgress';

  // Save progress to local storage
  const saveProgress = (step: number, cvData?: any, analysisData?: any, selectedJobs?: any[]) => {
    const progress = {
      activeStep: step,
      selectedCV: cvData,
      cvAnalysisResult: analysisData,
      selectedJobs: selectedJobs || selectedJobsData,
      timestamp: Date.now(),
    };
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(progress));
  };

  // Load progress from local storage
  const loadProgress = () => {
    try {
      const saved = localStorage.getItem(PROGRESS_STORAGE_KEY);
      if (saved) {
        const progress = JSON.parse(saved);
        // Check if progress is not too old (24 hours)
        const isRecent = Date.now() - progress.timestamp < 24 * 60 * 60 * 1000;
        if (isRecent) {
          return progress;
        }
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
    return null;
  };

  // Clear progress from local storage
  const clearProgress = () => {
    localStorage.removeItem(PROGRESS_STORAGE_KEY);
  };

  // Determine the appropriate starting step based on current state
  const determineStartingStep = () => {
    // If we have selected jobs, we should start from job selection or later
    if (selectedJobsData.length > 0) {
      // If we have CV analysis results, start from customization
      if (cvAnalysisResult) {
        return 3; // Customization step
      }
      // If we have a selected CV, start from job selection
      if (selectedCV) {
        return 2; // Job selection step
      }
      // If we have jobs but no CV, start from CV selection
      return 0; // CV selection step
    }
    
    // If we have CV analysis results but no jobs, start from job selection
    if (cvAnalysisResult && selectedCV) {
      return 2; // Job selection step
    }
    
    // If we have a selected CV but no analysis, start from CV analysis
    if (selectedCV && !cvAnalysisResult) {
      return 1; // CV analysis step
    }
    
    // Default to CV selection
    return 0;
  };

  // Initialize progress on modal open
  useEffect(() => {
    if (open) {
      const savedProgress = loadProgress();
      if (savedProgress) {
        setActiveStep(savedProgress.activeStep);
        if (savedProgress.selectedCV) {
          setSelectedCV(savedProgress.selectedCV);
        }
        if (savedProgress.cvAnalysisResult) {
          setCvAnalysisResult(savedProgress.cvAnalysisResult);
        }
        // Restore selected jobs from saved progress
        if (savedProgress.selectedJobs && savedProgress.selectedJobs.length > 0) {
          // Update the global store with saved jobs
          savedProgress.selectedJobs.forEach((job: any) => {
            if (!selectedJobs.includes(job.id)) {
              addSelectedJob(job.id, job);
            }
          });
        }
      } else {
        // Determine starting step based on current state
        const startingStep = determineStartingStep();
        setActiveStep(startingStep);
      }
    }
  }, [open, selectedJobsData.length, selectedCV, cvAnalysisResult, selectedJobs, addSelectedJob]);

  // Save progress whenever selectedJobsData changes
  useEffect(() => {
    if (open && selectedJobsData.length > 0) {
      saveProgress(activeStep, selectedCV, cvAnalysisResult, selectedJobsData);
    }
  }, [selectedJobsData, open, activeStep, selectedCV, cvAnalysisResult]);

  // Step navigation functions
  const handleNext = async () => {
    // If moving from step 0 to step 1, check for existing OCR data first
    if (activeStep === 0 && selectedCV) {
      const existingOCR = await checkExistingOCRData();
      
      if (existingOCR) {
        // Show popup asking user to choose
        setExistingOCRData(existingOCR);
        setShowOCRChoicePopup(true);
        return; // Don't proceed until user makes a choice
      } else {
        // No existing OCR data, proceed with new analysis
        const success = await processCVWithOCR();
        if (success) {
          const nextStep = activeStep + 1;
          setActiveStep(nextStep);
          // Save progress after successful CV analysis
          saveProgress(nextStep, selectedCV, cvAnalysisResult, selectedJobsData);
        }
        // Don't move to next step if OCR failed
      }
    } else if (activeStep === 1 && cvAnalysisResult) {
      // If moving from step 1 to step 2, upload OCR results first
      const success = await uploadOCRResults();
      if (success) {
        const nextStep = activeStep + 1;
        setActiveStep(nextStep);
        // Save progress after successful OCR upload
        saveProgress(nextStep, selectedCV, cvAnalysisResult, selectedJobsData);
      }
      // Don't move to next step if OCR upload failed
    } else {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      // Save progress for other steps
      saveProgress(nextStep, selectedCV, cvAnalysisResult, selectedJobsData);
    }
  };

  // Handle user choice for existing OCR data
  const handleUseExistingOCR = async () => {
    setShowOCRChoicePopup(false);
    setCvAnalysisResult(existingOCRData);
    setCvAnalysisError(null);
    
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    // Save progress after using existing OCR data
    saveProgress(nextStep, selectedCV, existingOCRData, selectedJobsData);
  };

  const handleCreateNewAnalysis = async () => {
    setShowOCRChoicePopup(false);
    setExistingOCRData(null);
    
    // Proceed with new OCR analysis
    const success = await processCVWithOCR();
    if (success) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      // Save progress after successful CV analysis
      saveProgress(nextStep, selectedCV, cvAnalysisResult, selectedJobsData);
    }
  };

  // Generate job application using Mistral AI
  const generateJobApplication = async (jobData: any): Promise<any> => {
    if (!cvAnalysisResult || !jobData) {
      throw new Error('Missing CV analysis or job data');
    }

    // Set loading state for this specific job
    setGeneratingApplication(jobData.id, true);

    try {
      const { data, error } = await supabase.functions.invoke('mistral-application', {
        body: {
          selectedCV: selectedCV,
          ocrData: cvAnalysisResult,
          jobData: jobData
        }
      });

      if (error) {
        // Log the full error details
        console.error('Supabase function error:', error);
        throw new Error(`Edge Function Error: ${error.message || 'Unknown error'} (Status: ${error.status || 'Unknown'})`);
      }

      if (!data.success) {
        // Log the response data for debugging
        console.error('Function returned error:', data);
        throw new Error(`Application Generation Failed: ${data.error || 'Unknown error'} (Details: ${JSON.stringify(data)})`);
      }

      // Store the generated application ID
      if (data.savedApplicationId) {
        setGeneratedApplicationStore(jobData.id, data.savedApplicationId);
      }

      // Return the raw edge function response
      return data;
    } catch (error) {
      console.error('Error generating job application:', error);
      throw error;
    } finally {
      // Clear loading state for this job
      setGeneratingApplication(jobData.id, false);
    }
  };

  // Fetch and display generated application
  const fetchGeneratedApplication = async (applicationId: string) => {
    setLoadingApplication(true);
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('id', applicationId)
        .single();

      if (error) {
        console.error('Error fetching application:', error);
        throw error;
      }

      setSelectedApplication(data);
      setShowApplicationModal(true);
    } catch (error) {
      console.error('Error fetching application:', error);
      setApplicationError('Failed to fetch application');
    } finally {
      setLoadingApplication(false);
    }
  };

  // Check for existing generated applications when modal opens
  useEffect(() => {
    if (open && selectedJobsData.length > 0) {
      checkExistingApplications();
    }
  }, [open, selectedJobsData]);

  const checkExistingApplications = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) return;

      // Check for existing applications for each selected job
      for (const job of selectedJobsData) {
        const { data: applications, error } = await supabase
          .from('applications')
          .select('id')
          .eq('user_id', user.id)
          .eq('product_id', job.id)
          .limit(1);

        if (!error && applications && applications.length > 0) {
          // Mark this job as having a generated application
          setGeneratedApplicationStore(job.id, applications[0].id);
        }
      }
    } catch (error) {
      console.error('Error checking existing applications:', error);
    }
  };

  // Check for existing OCR data
  const checkExistingOCRData = async (): Promise<any> => {
    if (!selectedCV?.id) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('documents')
        .select('ocr')
        .eq('id', selectedCV.id)
        .single();

      if (error) {
        console.error('Error checking existing OCR data:', error);
        return null;
      }

      return data?.ocr ? JSON.parse(data.ocr) : null;
    } catch (error) {
      console.error('Error parsing existing OCR data:', error);
      return null;
    }
  };

  // Upload OCR results to documents table
  const uploadOCRResults = async (): Promise<boolean> => {
    if (!selectedCV?.id || !cvAnalysisResult) {
      console.error('No CV selected or no analysis results available');
      return false;
    }

    setUploadingOCR(true);
    try {
      const { error } = await supabase
        .from('documents')
        .update({ 
          ocr: JSON.stringify(cvAnalysisResult) 
        })
        .eq('id', selectedCV.id);

      if (error) {
        console.error('Error uploading OCR results:', error);
        return false;
      }

      console.log('OCR results uploaded successfully');
      return true;
    } catch (error) {
      console.error('Error uploading OCR results:', error);
      return false;
    } finally {
      setUploadingOCR(false);
    }
  };

  // Process CV with OCR
  const processCVWithOCR = async (): Promise<boolean> => {
    if (!selectedCV?.url) {
      setCvAnalysisError('No document URL available');
      return false;
    }

    setProcessingCV(true);
    setCvAnalysisError(null);
    setCvAnalysisResult(null);

    try {
      console.log('Processing CV with OCR:', selectedCV.file_name);
      const result = await ApiKeysService.callMistralOCR(selectedCV.url, selectedCV.file_name);
      
        if (result.success && result.ocrResult) {
          setCvAnalysisResult(result.ocrResult);
          // Save progress when CV analysis is completed
          saveProgress(activeStep, selectedCV, result.ocrResult, selectedJobsData);
          console.log('CV analysis completed:', result.ocrResult);
          return true;
      } else {
        setCvAnalysisError(result.ocrResult?.userMessage || 'Failed to analyze CV');
        return false;
      }
    } catch (error) {
      console.error('Error processing CV:', error);
      setCvAnalysisError('Failed to process CV. Please try again.');
      return false;
    } finally {
      setProcessingCV(false);
    }
  };

  // Process text content to handle HTML and Markdown
  const processTextContent = (text: string) => {
    if (!text) return '';
    
    // First, convert common HTML tags to proper formatting
    let processedText = text
      // Convert <br> and <br/> to line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Convert <p> tags to double line breaks
      .replace(/<p[^>]*>/gi, '\n\n')
      .replace(/<\/p>/gi, '')
      // Convert <strong> and <b> to markdown bold
      .replace(/<(strong|b)[^>]*>/gi, '**')
      .replace(/<\/(strong|b)>/gi, '**')
      // Convert <em> and <i> to markdown italic
      .replace(/<(em|i)[^>]*>/gi, '*')
      .replace(/<\/(em|i)>/gi, '*')
      // Convert <h1> to markdown heading
      .replace(/<h1[^>]*>/gi, '# ')
      .replace(/<\/h1>/gi, '\n\n')
      // Convert <h2> to markdown heading
      .replace(/<h2[^>]*>/gi, '## ')
      .replace(/<\/h2>/gi, '\n\n')
      // Convert <h3> to markdown heading
      .replace(/<h3[^>]*>/gi, '### ')
      .replace(/<\/h3>/gi, '\n\n')
      // Convert <ul> and <li> to markdown lists
      .replace(/<ul[^>]*>/gi, '')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<li[^>]*>/gi, '- ')
      .replace(/<\/li>/gi, '\n')
      // Remove any remaining HTML tags
      .replace(/<[^>]*>/g, '')
      // Clean up multiple line breaks
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return processedText;
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    setActiveStep(prevStep);
    // Save progress when going back
    saveProgress(prevStep, selectedCV, cvAnalysisResult, selectedJobsData);
  };

  // Fetch favorized jobs directly from job_favorites table joined with company_jobs
  const fetchFavorizedJobs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setLoadingFavorizedJobs(true);
    try {
      console.log('Fetching favorized jobs for user:', user.id);

      // Fetch directly from job_favorites joined with company_jobs and companies
      const { data: favorizedJobs, error } = await supabase
        .from('job_favorites')
        .select(`
          *,
          job:company_jobs(
            *,
            company:companies(*)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching favorized jobs:', error);
        throw error;
      }

      console.log('Raw favorized jobs data:', favorizedJobs);

      // Filter out any entries where the job data is null (in case of deleted jobs)
      const validJobs = favorizedJobs?.filter(favJob => favJob.job) || [];
      
      console.log('Valid favorized jobs:', validJobs);
      setFavorizedJobs(validJobs);
    } catch (error) {
      console.error('Error fetching favorized jobs:', error);
      setFavorizedJobs([]);
    } finally {
      setLoadingFavorizedJobs(false);
    }
  };

  // Fetch job details for selected jobs
  const fetchSelectedJobDetails = async () => {
    console.log('fetchSelectedJobDetails called with selectedJobs:', selectedJobs);
    if (selectedJobs.length === 0) {
      console.log('No selected jobs, clearing job details');
      setSelectedJobDetails([]);
      return;
    }

    try {
      setLoadingJobDetails(true);
      console.log('Fetching job details for:', selectedJobs);
      const jobDetailsPromises = selectedJobs.map(jobId => 
        CompanyService.getJobById(jobId)
      );
      const jobDetails = await Promise.all(jobDetailsPromises);
      console.log('Fetched job details:', jobDetails);
      const filteredDetails = jobDetails.filter(job => job !== null);
      console.log('Filtered job details:', filteredDetails);
      setSelectedJobDetails(filteredDetails);
    } catch (error) {
      console.error('Error fetching selected job details:', error);
    } finally {
      setLoadingJobDetails(false);
    }
  };

  // Load favorized jobs when modal opens
  useEffect(() => {
    if (open) {
      fetchFavorizedJobs();
    }
  }, [open]);

  // Fetch job details when selected jobs change
  useEffect(() => {
    fetchSelectedJobDetails();
  }, [selectedJobs]);

  const handleReset = () => {
    setActiveStep(0);
    setSelectedCV(null);
    setSelectedJobCategory('');
    setJobTitle('');
    setCompanyName('');
    setJobDescription('');
    // Clear progress when resetting
    clearProgress();
  };

  const handleSubmitApplication = async () => {
    setIsCreating(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsCreating(false);
    // Clear progress when application is submitted
    clearProgress();
    onClose();
  };

  // Custom close handler that clears progress
  const handleClose = () => {
    // Only clear progress if we're not just minimizing
    if (!jobApplicationWriterMinimized) {
      clearProgress();
    }
    onClose();
  };

  const handleMinimize = () => {
    setIsClosing(true);
    // Wait for animation to complete before minimizing
    setTimeout(() => {
      setJobApplicationWriterMinimized(true);
      setIsClosing(false);
      // Notify parent component
      onMinimize?.();
    }, 400); // Animation duration
  };

  const handleRestore = () => {
    setJobApplicationWriterMinimized(false);
    // Notify parent component
    onRestore?.();
  };

  // Generation step functions
  const fetchJobDetails = async (jobId: string) => {
    setIsLoadingJobDetails(true);
    try {
      // Fetch real job details from products table using the correct column names
      const { data: jobData, error } = await supabase
        .from('products')
        .select(`
          *,
          categories!category_id(name),
          subcategories!subcategory_id(name)
        `)
        .eq('id', jobId)
        .eq('is_job', true)
        .single();

      if (error) {
        console.error('Error fetching job details:', error);
        throw error;
      }

      if (!jobData) {
        throw new Error('Job not found');
      }

      console.log('Fetched job data:', jobData);

      // Transform the data to match our expected format using actual database fields
      const jobDetails = {
        id: jobData.id,
        title: jobData.name, // Use 'name' field from products table
        company: jobData.company_id || 'Company Name', // Use company_id if available
        location: jobData.location || 'Location not specified',
        description: jobData.description || 'No description available',
        requirements: jobData.job_requirements || [
          'Bachelor\'s degree in Computer Science or related field',
          '3+ years of experience in software development',
          'Strong problem-solving skills',
          'Experience with modern web technologies',
          'Excellent communication skills'
        ],
        responsibilities: jobData.required_skills || [
          'Develop and maintain web applications',
          'Collaborate with cross-functional teams',
          'Write clean, maintainable code',
          'Participate in code reviews',
          'Mentor junior developers'
        ],
        benefits: jobData.benefits_perks || [
          'Competitive salary',
          'Health insurance',
          'Flexible working hours',
          'Professional development opportunities',
          'Remote work options'
        ],
        salary_min: jobData.price, // Use 'price' field as salary
        salary_max: jobData.price, // Use 'price' field as salary
        salary_currency: 'USD', // Default currency
        employment_type: jobData.job_type,
        job_type: jobData.job_type,
        experience_level: jobData.experience_level,
        is_remote: jobData.remote_work_allowed,
        contact_email: jobData.contact_email,
        application_deadline: jobData.application_deadline,
        expected_start_date: jobData.expected_start_date,
        duration: jobData.duration
      };
      
      setCurrentJobDetails(jobDetails);
    } catch (error) {
      console.error('Error fetching job details:', error);
      // Fallback to mock data if API fails
      const fallbackJobDetails = {
        id: jobId,
        title: selectedJobsData[currentJobIndex]?.title || 'Job Title',
        company: selectedJobsData[currentJobIndex]?.company || 'Company Name',
        location: selectedJobsData[currentJobIndex]?.location || 'Location',
        description: `This is a detailed job description for ${selectedJobsData[currentJobIndex]?.title || 'this position'}. The role involves working with cutting-edge technologies and collaborating with a talented team.`,
        requirements: [
          'Bachelor\'s degree in Computer Science or related field',
          '3+ years of experience in software development',
          'Strong problem-solving skills',
          'Experience with modern web technologies',
          'Excellent communication skills'
        ],
        responsibilities: [
          'Develop and maintain web applications',
          'Collaborate with cross-functional teams',
          'Write clean, maintainable code',
          'Participate in code reviews',
          'Mentor junior developers'
        ],
        benefits: [
          'Competitive salary',
          'Health insurance',
          'Flexible working hours',
          'Professional development opportunities',
          'Remote work options'
        ]
      };
      setCurrentJobDetails(fallbackJobDetails);
    } finally {
      setIsLoadingJobDetails(false);
    }
  };

  const generateApplication = async (jobDetails: any) => {
    // Check if application already exists for this job
    if (hasGeneratedApplication(jobDetails.id)) {
      // Check if the existing application is in draft or pending status
      const applicationId = getGeneratedApplicationId(jobDetails.id);
      if (applicationId) {
        try {
          const { data: existingApp, error } = await supabase
            .from('applications')
            .select('status')
            .eq('id', applicationId)
            .single();

          if (!error && existingApp && (existingApp.status === 'draft' || existingApp.status === 'pending')) {
            // Show confirmation modal to replace
            setJobToReplace(jobDetails);
            setShowReplaceConfirmModal(true);
            return null;
          } else if (!error && existingApp) {
            // Application exists but is not draft/pending
            setApplicationError(`An application for this job already exists with status: ${existingApp.status}. Only draft or pending applications can be replaced.`);
            setShowApplicationPopup(true);
            return null;
          }
        } catch (error) {
          console.error('Error checking existing application:', error);
        }
      }
    }

    // Proceed with generation
    await performGeneration(jobDetails);
  };

  const performGeneration = async (jobDetails: any) => {
    setIsGenerating(true);
    setApplicationError(null);
    setGeneratedApplicationLocal(null);
    
    try {
      // Generate application using Mistral AI
      const generatedApp = await generateJobApplication(jobDetails);
      
      // Show the application in popup
      setGeneratedApplicationLocal(generatedApp);
      setShowApplicationPopup(true);
      
      // Add to generated applications list
      const appWithId = {
        ...generatedApp,
        id: `app_${Date.now()}`,
        jobId: jobDetails.id,
        jobTitle: jobDetails.title,
        company: jobDetails.company,
      };
      
      setGeneratedApplications(prev => [...prev, appWithId]);
      return appWithId;
    } catch (error) {
      console.error('Error generating application:', error);
      setApplicationError(error instanceof Error ? error.message : 'Failed to generate application');
      setShowApplicationPopup(true);
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReplaceApplication = async () => {
    if (!jobToReplace) return;
    
    // Close the confirmation modal
    setShowReplaceConfirmModal(false);
    
    // Proceed with generation
    await performGeneration(jobToReplace);
    
    // Clear the job to replace
    setJobToReplace(null);
  };

  const handleNextJob = async () => {
    if (currentJobIndex < selectedJobsData.length - 1) {
      const nextIndex = currentJobIndex + 1;
      setCurrentJobIndex(nextIndex);
      setCurrentJobDetails(null);
      // Fetch details for next job using the correct index
      await fetchJobDetails(selectedJobsData[nextIndex].id);
    }
  };

  const handleNextJobOnly = async () => {
    if (currentJobIndex < selectedJobsData.length - 1) {
      // Just move to next job without generating
      await handleNextJob();
    } else {
      // Last job - move beyond the last job to show finish card
      setCurrentJobIndex(currentJobIndex + 1);
      setCurrentJobDetails(null);
    }
  };


  // Reset states when modal opens
  useEffect(() => {
    if (open) {
      setIsClosing(false);
      setIsMaximized(false);
      setIsMinimized(false);
      setCvAnalysisError(null);
      setProcessingCV(false);
    }
  }, [open]);

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Filter job applications based on search
  const filteredApplications = mockJobApplications.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'draft': return '#f59e0b';
      case 'error': return '#ef4444';
      case 'building': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckIcon />;
      case 'draft': return <EditIcon />;
      case 'error': return <ErrorIcon />;
      case 'building': return <SettingsIcon />;
      default: return <ScheduleIcon />;
    }
  };

  // Fetch user documents from Supabase
  const fetchUserDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        return;
      }

      setUserDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Handle document selection from MyDocumentsModal
  const handleDocumentSelect = (document: UserDocument) => {
    setSelectedCV(document);
    setShowDocumentsModal(false);
    // Save progress when CV is selected
    saveProgress(activeStep, document, cvAnalysisResult, selectedJobsData);
    if (onDocumentSelect) {
      onDocumentSelect(document);
    }
  };

  // Load documents when modal opens
  useEffect(() => {
    if (open) {
      fetchUserDocuments();
    }
  }, [open]);

  // Set initial document if provided
  useEffect(() => {
    if (document && !selectedCV) {
      setSelectedCV(document);
    }
  }, [document, selectedCV]);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file type icon
  const getFileTypeIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <DocumentIcon />;
    if (fileType.includes('word') || fileType.includes('document')) return <DocumentIcon />;
    if (fileType.includes('image')) return <DocumentIcon />;
    return <DocumentIcon />;
  };

  return (
    <>
        <Dialog
          open={open && !jobApplicationWriterMinimized}
          onClose={handleClose}
        maxWidth={isMaximized ? false : "lg"}
        fullWidth={!isMaximized}
        fullScreen={isMaximized}
        PaperProps={{
          sx: {
            borderRadius: isMaximized ? 0 : 3,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            minHeight: isMaximized ? '100vh' : '80vh',
            maxHeight: isMaximized ? '100vh' : '90vh',
            boxShadow: isMaximized ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden', // Prevent content from showing during animation
          },
        }}
      >
      <motion.div
        initial={false}
        animate={isClosing ? {
          opacity: 0,
          scale: 0.95,
        } : {
          opacity: 1,
          scale: 1,
        }}
        transition={{
          duration: 0.4,
          ease: [0.25, 0.46, 0.45, 0.94], // Beautiful smooth easing
          opacity: {
            duration: 0.4,
            ease: "easeOut"
          },
          scale: {
            duration: 0.4,
            ease: "easeOut"
          }
        }}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CodeIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
             <Box>
               <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                 Job Application Writer
               </Typography>
               <Typography variant="body2" sx={{ color: '#64748b' }}>
                 Create and manage your job applications
               </Typography>
             </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={handleMinimize} 
              sx={{ 
                color: '#64748b',
                '&:hover': {
                  backgroundColor: alpha('#f59e0b', 0.1),
                  color: '#f59e0b',
                },
              }}
              title="Minimize"
            >
              <MinimizeIcon />
            </IconButton>
            <IconButton 
              onClick={handleMaximize} 
              sx={{ 
                color: '#64748b',
                '&:hover': {
                  backgroundColor: alpha('#10b981', 0.1),
                  color: '#10b981',
                },
              }}
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? <RestoreIcon /> : <MaximizeIcon />}
            </IconButton>
              <IconButton onClick={handleClose} sx={{ color: '#64748b' }}>
                <CloseIcon />
              </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ 
        p: 0, 
        display: 'flex', 
        flex: 1,
        overflow: 'hidden',
        height: isMaximized ? 'calc(100vh - 120px)' : '70vh'
      }}>
        {/* Left Sidebar - Applications Management */}
        {showSidebar && (
          <Paper
            elevation={1}
            sx={{
              width: '350px',
              borderRadius: 0,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRight: '1px solid #e2e8f0',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Sidebar Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                   My Job Applications
                 </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowSidebar(false)}
                  sx={{ 
                    color: '#64748b',
                    backgroundColor: alpha('#64748b', 0.1),
                    '&:hover': {
                      backgroundColor: alpha('#64748b', 0.2),
                      color: '#ef4444',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* Search */}
               <TextField
                 fullWidth
                 placeholder="Search job applications..."
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                  },
                }}
              />
            </Box>

            {/* Selected Jobs List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ px: 2, py: 1.5, fontWeight: 600, color: '#64748b', backgroundColor: alpha('#8b5cf6', 0.05) }}>
                🎯 Selected Jobs ({selectedJobsCount})
              </Typography>
              {selectedJobsCount === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <WorkIcon sx={{ color: 'white', fontSize: 32 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                    No jobs selected yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                    Choose jobs from the job listings to get started with your application
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => {
                      handleMinimize();
                      setTimeout(() => {
                        navigate('/all-jobs');
                      }, 100);
                    }}
                    sx={{
                      borderColor: '#8b5cf6',
                      color: '#8b5cf6',
                      '&:hover': {
                        borderColor: '#7c3aed',
                        backgroundColor: alpha('#8b5cf6', 0.05),
                      },
                    }}
                  >
                    Browse Jobs
                  </Button>
                </Box>
              ) : selectedJobsData.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Debug: selectedJobsData.length = {selectedJobsData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    selectedJobs: {JSON.stringify(selectedJobs)}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {selectedJobsData.map((job, index) => (
                    <motion.div
                      key={job.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <ListItem
                        component="div"
                          onClick={() => {
                            // Navigate to job detail page
                            navigate(`/product/${job.id}`);
                          }}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          borderBottom: index < selectedJobsData.length - 1 ? '1px solid #f1f5f9' : 'none',
                          '&:hover': {
                            backgroundColor: alpha('#8b5cf6', 0.05),
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {job.title}
                              </Typography>
                              {job.isRemote && (
                                <Chip
                                  label="Remote"
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha('#10b981', 0.1),
                                    color: '#10b981',
                                    fontSize: '0.7rem',
                                    height: 18,
                                  }}
                                />
                              )}
                              {isGeneratingApplication(job.id) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LoadingSpinner size={16} variant="spinner" color="#8b5cf6" />
                                  <Typography variant="caption" sx={{ color: '#8b5cf6', fontSize: '0.7rem' }}>
                                    Generating...
                                  </Typography>
                                </Box>
                              )}
                              {hasGeneratedApplication(job.id) && !isGeneratingApplication(job.id) && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    cursor: 'pointer',
                                    padding: '2px 6px',
                                    borderRadius: 1,
                                    backgroundColor: alpha('#10b981', 0.1),
                                    border: `1px solid ${alpha('#10b981', 0.2)}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: alpha('#10b981', 0.15),
                                      borderColor: alpha('#10b981', 0.3),
                                      transform: 'scale(1.05)',
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const applicationId = getGeneratedApplicationId(job.id);
                                    if (applicationId) {
                                      fetchGeneratedApplication(applicationId);
                                    }
                                  }}
                                >
                                  <CheckIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                  <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 500 }}>
                                    Generated
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.75rem', color: '#94a3b8' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOnIcon sx={{ fontSize: 14 }} />
                                {job.location || 'Location TBD'}
                              </Box>
                              {job.salary && job.salary.min && job.salary.max && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AttachMoneyIcon sx={{ fontSize: 14 }} />
                                  {job.salary.currency} {job.salary.min.toLocaleString()} - {job.salary.max.toLocaleString()}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                         {activeStep < 3 && !isGeneratingApplication(job.id) && (
                          <ListItemSecondaryAction>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSelectedJob(job.id);
                              }}
                              sx={{ 
                                color: '#ef4444',
                                '&:hover': {
                                  backgroundColor: alpha('#ef4444', 0.1),
                                },
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    </motion.div>
                  ))}
                </List>
              )}
            </Box>

            {/* Sidebar Footer */}
            <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                 onClick={() => {
                   setSelectedApp(null);
                   setActiveStep(0);
                   setSelectedCV(null);
                   setSelectedJobCategory('');
                   setJobTitle('');
                   setCompanyName('');
                   setJobDescription('');
                 }}
                sx={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                  },
                }}
               >
                 Start New Application
               </Button>
            </Box>
          </Paper>
        )}

        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!showSidebar && (
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid #e2e8f0', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            }}>
              <IconButton
                onClick={() => setShowSidebar(true)}
                sx={{ 
                  color: '#8b5cf6',
                  backgroundColor: alpha('#8b5cf6', 0.1),
                  '&:hover': {
                    backgroundColor: alpha('#8b5cf6', 0.2),
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <AppsIcon />
              </IconButton>
               <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                 Job Application Writer
               </Typography>
               <Box sx={{ ml: 'auto' }}>
                 <Typography variant="body2" sx={{ color: '#64748b' }}>
                   {selectedJobsCount} job{selectedJobsCount !== 1 ? 's' : ''} selected
                 </Typography>
               </Box>
            </Box>
          )}

          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {selectedApp ? (
              /* Application Details View */
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: alpha(selectedApp.color, 0.1),
                      color: selectedApp.color,
                    }}
                  >
                    {selectedApp.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {selectedApp.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {selectedApp.description}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<PlayIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Run Application
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    sx={{
                      borderColor: '#e2e8f0',
                      color: '#64748b',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    sx={{
                      borderColor: '#e2e8f0',
                      color: '#64748b',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Settings
                  </Button>
                </Box>

                <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                  Application details and configuration will be displayed here. This is a preview of the interface.
                </Typography>
              </Paper>
            ) : (
              /* Multi-Step Creation Process */
              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {/* Progress Indicator */}
                <Box sx={{ mb: 4 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(activeStep / (steps.length - 1)) * 100} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      backgroundColor: alpha('#e2e8f0', 0.3),
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        borderRadius: 3,
                      },
                    }} 
                  />
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 1, textAlign: 'center' }}>
                    Step {activeStep + 1} of {steps.length}: {steps[activeStep]?.title}
                  </Typography>
                </Box>

                {/* Stepper */}
                <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
                  {steps.map((step, index) => (
                    <Step key={step.id}>
                      <StepLabel
                        StepIconComponent={({ active, completed }) => (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: completed 
                                ? '#10b981' 
                                : active 
                                  ? '#8b5cf6' 
                                  : '#e2e8f0',
                              color: completed || active ? 'white' : '#94a3b8',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {completed ? <CheckIcon /> : step.icon}
                          </Box>
                        )}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {step.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                            {step.description}
                          </Typography>
                        </Box>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {/* Step Content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
              {activeStep === 0 && (
                <Paper
                  elevation={1}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                    Select Your CV
                  </Typography>
                   <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                     Choose a document from your existing files to use for your job application.
                   </Typography>

                  {selectedCV ? (
                    /* Selected Document Display */
                    <Card
                      sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        border: '2px solid #8b5cf6',
                        backgroundColor: alpha('#8b5cf6', 0.05),
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ backgroundColor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }}>
                          {getFileTypeIcon(selectedCV.file_type)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {selectedCV.file_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            {selectedCV.description || 'No description available'}
                          </Typography>
                        </Box>
                        <Chip
                          label="Selected"
                          size="small"
                          sx={{
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            fontSize: '0.75rem',
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            📄 {selectedCV.file_type}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            📏 {formatFileSize(selectedCV.file_size)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            📅 {new Date(selectedCV.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Processing State */}
                      {processingCV ? (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          py: 4,
                          backgroundColor: alpha('#8b5cf6', 0.05),
                          borderRadius: 2,
                          border: '1px dashed #8b5cf6',
                        }}>
                          <CircularProgress 
                            size={50} 
                            sx={{ 
                              color: '#8b5cf6',
                              mb: 2,
                            }} 
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                            🔍 Analyzing Your CV...
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', mb: 2 }}>
                            Our AI is extracting and analyzing the content from your CV document.
                            <br />
                            This may take a few moments.
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: '#8b5cf6',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1, transform: 'scale(1)' },
                                '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                                '100%': { opacity: 1, transform: 'scale(1)' },
                              }
                            }} />
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: '#8b5cf6',
                              animation: 'pulse 1.5s ease-in-out infinite 0.2s',
                            }} />
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: '#8b5cf6',
                              animation: 'pulse 1.5s ease-in-out infinite 0.4s',
                            }} />
                          </Box>
                        </Box>
                      ) : cvAnalysisError ? (
                        /* Error State */
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          py: 3,
                          backgroundColor: alpha('#ef4444', 0.05),
                          borderRadius: 2,
                          border: '1px solid #ef4444',
                        }}>
                          <ErrorIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                            Analysis Failed
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', mb: 2 }}>
                            {cvAnalysisError}
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={processCVWithOCR}
                            startIcon={<RefreshIcon />}
                            size="small"
                            sx={{
                              borderColor: '#8b5cf6',
                              color: '#8b5cf6',
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            Try Again
                          </Button>
                        </Box>
                      ) : (
                        /* Success State - Show action buttons */
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => setShowDocumentsModal(true)}
                            sx={{
                              borderColor: '#8b5cf6',
                              color: '#8b5cf6',
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            Choose Another Document
                          </Button>
                          {selectedCV.url && (
                            <Button
                              variant="outlined"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => window.open(selectedCV.url, '_blank')}
                              sx={{
                                borderColor: '#e2e8f0',
                                color: '#64748b',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                              }}
                            >
                              Preview Document
                            </Button>
                          )}
                        </Box>
                      )}
                    </Card>
                  ) : (
                    /* No Document Selected */
                    <Card
                      sx={{
                        p: 4,
                        mb: 3,
                        border: '2px dashed #e2e8f0',
                        borderRadius: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#8b5cf6',
                          backgroundColor: alpha('#8b5cf6', 0.05),
                        },
                      }}
                      onClick={() => setShowDocumentsModal(true)}
                    >
                      <CloudUploadIcon sx={{ fontSize: 48, color: '#8b5cf6', mb: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                        Select a Document
                      </Typography>
                       <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                         Choose from your existing documents to use for your job application
                       </Typography>
                      <Button
                        variant="contained"
                        startIcon={<DocumentIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                          },
                        }}
                      >
                        Browse Documents
                      </Button>
                    </Card>
                  )}

                  {/* Recent Documents Preview */}
                  {userDocuments.length > 0 && !selectedCV && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                        Recent Documents:
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {userDocuments.slice(0, 3).map((doc, index) => (
                          <ListItem
                            key={doc.id}
                            component="div"
                              onClick={() => {
                                setSelectedCV(doc);
                                // Save progress when CV is selected
                                saveProgress(activeStep, doc, cvAnalysisResult, selectedJobsData);
                              }}
                            sx={{
                              p: 2,
                              mb: 1,
                              borderRadius: 2,
                              cursor: 'pointer',
                              border: '1px solid #e2e8f0',
                              backgroundColor: 'transparent',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: alpha('#8b5cf6', 0.05),
                                borderColor: '#8b5cf6',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 48 }}>
                              <Avatar sx={{ backgroundColor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }}>
                                {getFileTypeIcon(doc.file_type)}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {doc.file_name}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {doc.description || 'No description available'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                    {formatFileSize(doc.file_size)} • {new Date(doc.created_at).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Step 1: CV Analysis Results */}
              {activeStep === 1 && cvAnalysisResult && (
                <Paper
                  elevation={1}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                    ✅ CV Analysis Results
                  </Typography>
                  
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                    Your CV has been successfully analyzed. Here's what we found:
                  </Typography>

                   {/* CV Content Display */}
                   {cvAnalysisResult.extractedText && (
                     <Box sx={{ mb: 4 }}>
                       <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                         📄 CV Content
                       </Typography>
                       <Paper
                         elevation={1}
                         sx={{
                           p: 3,
                           borderRadius: 2,
                           backgroundColor: alpha('#f8fafc', 0.5),
                           border: '1px solid #e2e8f0',
                           maxHeight: '400px',
                           overflow: 'auto',
                         }}
                       >
                         <ReactMarkdown
                           components={{
                             h1: ({ children }) => (
                               <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 2 }}>
                                 {children}
                               </Typography>
                             ),
                             h2: ({ children }) => (
                               <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 2 }}>
                                 {children}
                               </Typography>
                             ),
                             h3: ({ children }) => (
                               <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1, mt: 2 }}>
                                 {children}
                               </Typography>
                             ),
                             strong: ({ children }) => (
                               <Typography component="span" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                                 {children}
                               </Typography>
                             ),
                             em: ({ children }) => (
                               <Typography component="span" sx={{ fontStyle: 'italic', color: '#64748b' }}>
                                 {children}
                               </Typography>
                             ),
                             p: ({ children }) => (
                               <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
                                 {children}
                               </Typography>
                             ),
                             ul: ({ children }) => (
                               <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                                 {children}
                               </Box>
                             ),
                             ol: ({ children }) => (
                               <Box component="ol" sx={{ pl: 2, mb: 1 }}>
                                 {children}
                               </Box>
                             ),
                             li: ({ children }) => (
                               <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                 {children}
                               </Typography>
                             ),
                             a: ({ children, href }) => (
                               <Typography 
                                 component="a" 
                                 href={href} 
                                 target="_blank" 
                                 rel="noopener noreferrer"
                                 sx={{ 
                                   color: '#3b82f6', 
                                   textDecoration: 'none',
                                   '&:hover': { textDecoration: 'underline' }
                                 }}
                               >
                                 {children}
                               </Typography>
                             ),
                             table: ({ children }) => (
                               <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                                 <Table size="small">
                                   {children}
                                 </Table>
                               </TableContainer>
                             ),
                             thead: ({ children }) => (
                               <TableHead>
                                 {children}
                               </TableHead>
                             ),
                             tbody: ({ children }) => (
                               <TableBody>
                                 {children}
                               </TableBody>
                             ),
                             tr: ({ children }) => (
                               <TableRow>
                                 {children}
                               </TableRow>
                             ),
                             th: ({ children }) => (
                               <TableCell sx={{ fontWeight: 'bold', backgroundColor: alpha('#f1f5f9', 0.5) }}>
                                 {children}
                               </TableCell>
                             ),
                             td: ({ children }) => (
                               <TableCell>
                                 {children}
                               </TableCell>
                             ),
                             code: ({ children }) => (
                               <Typography 
                                 component="code" 
                                 sx={{ 
                                   backgroundColor: alpha('#f1f5f9', 0.8), 
                                   px: 1, 
                                   py: 0.5, 
                                   borderRadius: 1,
                                   fontFamily: 'monospace',
                                   fontSize: '0.875rem'
                                 }}
                               >
                                 {children}
                               </Typography>
                             ),
                             blockquote: ({ children }) => (
                               <Box 
                                 sx={{ 
                                   borderLeft: '4px solid #e2e8f0', 
                                   pl: 2, 
                                   ml: 2, 
                                   mb: 2,
                                   fontStyle: 'italic',
                                   color: '#64748b'
                                 }}
                               >
                                 {children}
                               </Box>
                             ),
                           }}
                         >
                           {processTextContent(cvAnalysisResult.extractedText)}
                         </ReactMarkdown>
                       </Paper>
                     </Box>
                   )}

                   {/* Formatting Information */}
                   {cvAnalysisResult.formattingInfo && (
                     <Box sx={{ mb: 4 }}>
                       <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                         📋 Document Structure
                       </Typography>
                       <Paper
                         elevation={1}
                         sx={{
                           p: 3,
                           borderRadius: 2,
                           backgroundColor: alpha('#3b82f6', 0.05),
                           border: `1px solid ${alpha('#3b82f6', 0.2)}`,
                         }}
                       >
                         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                           {cvAnalysisResult.formattingInfo.headings && cvAnalysisResult.formattingInfo.headings.length > 0 && (
                             <Box>
                               <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                                 📝 Headings Found:
                               </Typography>
                               <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                 {cvAnalysisResult.formattingInfo.headings.map((heading: any, index: number) => (
                                   <Chip
                                     key={index}
                                     label={`H${heading.level}: ${heading.text}`}
                                     size="small"
                                     sx={{
                                       backgroundColor: alpha('#1e40af', 0.1),
                                       color: '#1e40af',
                                       fontSize: '0.75rem',
                                     }}
                                   />
                                 ))}
                               </Box>
                             </Box>
                           )}
                           
                           {cvAnalysisResult.formattingInfo.boldText && cvAnalysisResult.formattingInfo.boldText.length > 0 && (
                             <Box>
                               <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#059669' }}>
                                 ** Bold Text Found:
                               </Typography>
                               <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                 {cvAnalysisResult.formattingInfo.boldText.slice(0, 10).map((bold: any, index: number) => (
                                   <Chip
                                     key={index}
                                     label={bold.text}
                                     size="small"
                                     sx={{
                                       backgroundColor: alpha('#059669', 0.1),
                                       color: '#059669',
                                       fontSize: '0.75rem',
                                     }}
                                   />
                                 ))}
                                 {cvAnalysisResult.formattingInfo.boldText.length > 10 && (
                                   <Chip
                                     label={`+${cvAnalysisResult.formattingInfo.boldText.length - 10} more`}
                                     size="small"
                                     sx={{
                                       backgroundColor: alpha('#6b7280', 0.1),
                                       color: '#6b7280',
                                       fontSize: '0.75rem',
                                     }}
                                   />
                                 )}
                               </Box>
                             </Box>
                           )}
                           
                           {cvAnalysisResult.formattingInfo.lists && cvAnalysisResult.formattingInfo.lists.length > 0 && (
                             <Box>
                               <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7c3aed' }}>
                                 📋 Lists Found:
                               </Typography>
                               <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                 {cvAnalysisResult.formattingInfo.lists.map((list: any, index: number) => (
                                   <Chip
                                     key={index}
                                     label={`${list.type === 'ordered' ? 'Ordered' : 'Unordered'} List (${list.items.length} items)`}
                                     size="small"
                                     sx={{
                                       backgroundColor: alpha('#7c3aed', 0.1),
                                       color: '#7c3aed',
                                       fontSize: '0.75rem',
                                     }}
                                   />
                                 ))}
                               </Box>
                             </Box>
                           )}
                         </Box>
                       </Paper>
                     </Box>
                   )}

                  {/* Processing Info */}
                  <Box sx={{ p: 2, backgroundColor: alpha('#f1f5f9', 0.5), borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                      ✅ Analysis Complete
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Pages processed: {cvAnalysisResult.pagesProcessed || 'N/A'} • 
                      Model: {cvAnalysisResult.model || 'N/A'} • 
                      Processing time: {cvAnalysisResult.processingTime ? new Date(cvAnalysisResult.processingTime).toLocaleTimeString() : 'N/A'}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {activeStep === 2 && (
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <WorkIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Job Selection
                    </Typography>
                  </Box>
                  
                  {selectedJobsData.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                        Selected Jobs ({selectedJobsData.length})
                      </Typography>
                      
                      {selectedJobsData.map((job, index) => (
                        <Paper
                          key={job.id}
                          elevation={2}
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                            border: '1px solid #e2e8f0',
                            '&:hover': {
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                                {job.title}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOnIcon sx={{ color: '#64748b', fontSize: 16 }} />
                                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {job.location}
                                  </Typography>
                                </Box>
                                {job.salary && job.salary.min && job.salary.max && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AttachMoneyIcon sx={{ color: '#64748b', fontSize: 16 }} />
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                      {job.salary.currency} {job.salary.min.toLocaleString()} - {job.salary.max.toLocaleString()}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {job.isRemote && (
                                  <Chip
                                    label="Remote"
                                    size="small"
                                    sx={{ 
                                      backgroundColor: alpha('#10b981', 0.1), 
                                      color: '#10b981',
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                )}
                                {job.employmentType && (
                                  <Chip
                                    label={job.employmentType}
                                    size="small"
                                    sx={{ 
                                      backgroundColor: alpha('#3b82f6', 0.1), 
                                      color: '#3b82f6',
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                )}
                                {job.experienceLevel && (
                                  <Chip
                                    label={job.experienceLevel}
                                    size="small"
                                    sx={{ 
                                      backgroundColor: alpha('#f59e0b', 0.1), 
                                      color: '#f59e0b',
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                             {activeStep < 3 && (
                               <IconButton
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   removeSelectedJob(job.id);
                                 }}
                                 sx={{
                                   color: '#ef4444',
                                   '&:hover': {
                                     backgroundColor: alpha('#ef4444', 0.1),
                                   },
                                 }}
                               >
                                 <CloseIcon />
                               </IconButton>
                             )}
                          </Box>
                        </Paper>
                      ))}
                      
                      <Button
                        variant="outlined"
                        onClick={() => {
                          handleMinimize();
                          setTimeout(() => {
                            navigate('/all-jobs');
                          }, 100);
                        }}
                        startIcon={<SearchIcon />}
                        sx={{
                          borderColor: '#3b82f6',
                          color: '#3b82f6',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            borderColor: '#3b82f6',
                            color: '#3b82f6',
                            backgroundColor: alpha('#3b82f6', 0.05),
                          },
                        }}
                      >
                        Add More Jobs
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <WorkIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
                      </Box>
                      
                      <Typography variant="h6" sx={{ color: '#1e293b', mb: 2, fontWeight: 600 }}>
                        No Job Selected
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#64748b', mb: 4, maxWidth: 400, mx: 'auto' }}>
                        Choose how you'd like to find your next opportunity
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            // Minimize the popup to show the widget
                            handleMinimize();
                            // Navigate to dashboard with favorites tab after a short delay
                            setTimeout(() => {
                              navigate('/dashboard?tab=favorites&mode=jobs');
                            }, 100);
                          }}
                          startIcon={<FavoriteIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.3)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                              boxShadow: '0 6px 8px -1px rgba(139, 92, 246, 0.4)',
                              transform: 'translateY(-2px)',
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                            },
                          }}
                        >
                          Choose from Favorites
                        </Button>
                        
                        <Button
                          variant="outlined"
                          onClick={() => {
                            // Minimize the popup to show the widget
                            handleMinimize();
                            // Navigate to all jobs page after a short delay
                            setTimeout(() => {
                              navigate('/all-jobs');
                            }, 100);
                          }}
                          startIcon={<SearchIcon />}
                          sx={{
                            borderColor: '#3b82f6',
                            color: '#3b82f6',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            borderWidth: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#1d4ed8',
                              color: '#1d4ed8',
                              backgroundColor: alpha('#3b82f6', 0.05),
                              borderWidth: 2,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                            },
                          }}
                        >
                          Open Job Search
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Step 3: AI Generation */}
              {activeStep === 3 && (
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                    
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
                      Generate AI Applications
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4, maxWidth: 500, mx: 'auto' }}>
                      Ready to generate personalized AI applications for your {selectedJobsData.length} selected job{selectedJobsData.length !== 1 ? 's' : ''}? 
                      Our AI will create tailored applications based on your CV and each job's requirements.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                      {selectedJobsData.map((job, index) => (
                        <Paper
                          key={job.id}
                          elevation={1}
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                              {job.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOnIcon sx={{ color: '#64748b', fontSize: 16 }} />
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                  {job.location}
                                </Typography>
                              </Box>
                              {job.salary && job.salary.min && job.salary.max && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AttachMoneyIcon sx={{ color: '#64748b', fontSize: 16 }} />
                                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {job.salary.currency} {job.salary.min.toLocaleString()} - {job.salary.max.toLocaleString()}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {job.isRemote && (
                                <Chip
                                  label="Remote"
                                  size="small"
                                  sx={{ 
                                    backgroundColor: alpha('#10b981', 0.1), 
                                    color: '#10b981',
                                    fontSize: '0.75rem',
                                  }}
                                />
                              )}
                              {job.employmentType && (
                                <Chip
                                  label={job.employmentType}
                                  size="small"
                                  sx={{ 
                                    backgroundColor: alpha('#3b82f6', 0.1), 
                                    color: '#3b82f6',
                                    fontSize: '0.75rem',
                                  }}
                                />
                              )}
                              {job.experienceLevel && (
                                <Chip
                                  label={job.experienceLevel}
                                  size="small"
                                  sx={{ 
                                    backgroundColor: alpha('#f59e0b', 0.1), 
                                    color: '#f59e0b',
                                    fontSize: '0.75rem',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                              Ready
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          handleMinimize();
                          setTimeout(() => {
                            navigate('/all-jobs');
                          }, 100);
                        }}
                        startIcon={<SearchIcon />}
                        sx={{
                          borderColor: '#64748b',
                          color: '#64748b',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            borderColor: '#3b82f6',
                            color: '#3b82f6',
                            backgroundColor: alpha('#3b82f6', 0.05),
                          },
                        }}
                      >
                        Add More Jobs
                      </Button>
                      <Button
                        variant="contained"
                        onClick={async () => {
                          if (selectedJobsData.length > 0) {
                            setCurrentJobIndex(0);
                            setGeneratedApplications([]);
                            setIsLoadingJobDetails(true); // Start loading immediately
                            setActiveStep(4); // Navigate to Done step
                            // Now fetch the details for the first job
                            await fetchJobDetails(selectedJobsData[0].id);
                            setIsLoadingJobDetails(false); // End loading after fetch completes
                          }
                        }}
                        startIcon={<AutoAwesomeIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 4,
                          py: 1.5,
                          boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            boxShadow: '0 6px 8px -1px rgba(139, 92, 246, 0.4)',
                            transform: 'translateY(-2px)',
                          },
                          '&:active': {
                            transform: 'translateY(0px)',
                          },
                        }}
                      >
                        Generate Applications
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )}

              {/* Step 4: Done */}
              {activeStep === 4 && (
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
                      🚀 Generating Applications
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                      Job {currentJobIndex + 1} of {selectedJobsData.length}: {selectedJobsData[currentJobIndex]?.title}
                    </Typography>
                  </Box>

                  {isLoadingJobDetails ? (
                    // Loading job details
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      py: 6,
                      backgroundColor: alpha('#8b5cf6', 0.05),
                      borderRadius: 2,
                      border: '1px dashed #8b5cf6',
                    }}>
                      <CircularProgress 
                        size={60} 
                        sx={{ 
                          color: '#8b5cf6',
                          mb: 3,
                        }} 
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                        🔍 Loading Job Details...
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', mb: 3 }}>
                        Fetching detailed information for this position
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: '#8b5cf6',
                          animation: 'pulse 1.5s ease-in-out infinite',
                        }} />
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: '#8b5cf6',
                          animation: 'pulse 1.5s ease-in-out infinite 0.2s',
                        }} />
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: '#8b5cf6',
                          animation: 'pulse 1.5s ease-in-out infinite 0.4s',
                        }} />
                      </Box>
                    </Box>
                  ) : currentJobDetails ? (
                    // Job details loaded - show job card
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 4,
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                          border: '2px solid #3b82f6',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                              {currentJobDetails.title}
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#64748b', fontWeight: 500, mb: 2 }}>
                              {currentJobDetails.company}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOnIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                                  {currentJobDetails.location}
                                </Typography>
                              </Box>
                              {currentJobDetails.salary_min && currentJobDetails.salary_max && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AttachMoneyIcon sx={{ color: '#64748b', fontSize: 20 }} />
                                  <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                                    {currentJobDetails.salary_currency || 'USD'} {currentJobDetails.salary_min?.toLocaleString()} - {currentJobDetails.salary_max?.toLocaleString()}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            {(currentJobDetails.employment_type || currentJobDetails.job_type || currentJobDetails.experience_level) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                {currentJobDetails.employment_type && (
                                  <Chip
                                    label={currentJobDetails.employment_type}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#3b82f6',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                )}
                                {currentJobDetails.job_type && (
                                  <Chip
                                    label={currentJobDetails.job_type}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#8b5cf6',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                )}
                                {currentJobDetails.experience_level && (
                                  <Chip
                                    label={currentJobDetails.experience_level}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#f59e0b',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                )}
                                {currentJobDetails.is_remote && (
                                  <Chip
                                    label="Remote"
                                    size="small"
                                    sx={{
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                            📝 Job Description
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.6, mb: 3 }}>
                            {currentJobDetails.description}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 4 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                              ✅ Requirements
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {currentJobDetails.requirements.map((req: string, index: number) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    backgroundColor: '#10b981',
                                    flexShrink: 0,
                                  }} />
                                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {req}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                              🎯 Responsibilities
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {currentJobDetails.responsibilities.map((resp: string, index: number) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    backgroundColor: '#3b82f6',
                                    flexShrink: 0,
                                  }} />
                                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {resp}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            onClick={() => setActiveStep(3)}
                            sx={{
                              borderColor: '#64748b',
                              color: '#64748b',
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: '#3b82f6',
                                color: '#3b82f6',
                                backgroundColor: alpha('#3b82f6', 0.05),
                              },
                            }}
                          >
                            Back to Selection
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => generateApplication(currentJobDetails)}
                            disabled={isGenerating}
                            startIcon={isGenerating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                            sx={{
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 4,
                              py: 1.5,
                              boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                boxShadow: '0 6px 8px -1px rgba(139, 92, 246, 0.4)',
                                transform: 'translateY(-2px)',
                              },
                              '&:active': {
                                transform: 'translateY(0px)',
                              },
                            }}
                          >
                            {isGenerating ? 'Generating Application...' : 'Generate Application'}
                          </Button>
                          {hasGeneratedApplication(currentJobDetails.id) && (
                            <Button
                              variant="contained"
                              onClick={() => {
                                const applicationId = getGeneratedApplicationId(currentJobDetails.id);
                                if (applicationId) {
                                  fetchGeneratedApplication(applicationId);
                                }
                              }}
                              disabled={loadingApplication}
                              startIcon={loadingApplication ? <CircularProgress size={20} /> : <DescriptionIcon />}
                              sx={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                  boxShadow: '0 6px 8px -1px rgba(16, 185, 129, 0.4)',
                                  transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                  transform: 'translateY(0px)',
                                },
                              }}
                            >
                              {loadingApplication ? 'Loading...' : 'Show Application'}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            onClick={handleNextJobOnly}
                            disabled={isGenerating}
                            startIcon={isGenerating ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                            sx={{
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 4,
                              py: 1.5,
                              boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                boxShadow: '0 6px 8px -1px rgba(139, 92, 246, 0.4)',
                                transform: 'translateY(-2px)',
                              },
                              '&:active': {
                                transform: 'translateY(0px)',
                              },
                            }}
                          >
                            {isGenerating ? 'Generating...' : 'Next'}
                          </Button>
                        </Box>
                      </Paper>
                    </Box>
                  ) : currentJobIndex >= selectedJobsData.length ? (
                    // All jobs processed - show finish card
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      py: 6,
                      backgroundColor: alpha('#10b981', 0.05),
                      borderRadius: 2,
                      border: '1px solid #10b981',
                    }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
                        🎉 All Done!
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#64748b', mb: 4, maxWidth: 500, mx: 'auto', textAlign: 'center' }}>
                        Successfully generated applications for all {selectedJobsData.length} selected jobs. 
                        Your applications are ready to use!
                      </Typography>
                    </Box>
                  ) : null}
                </Paper>
              )}


                  </motion.div>
                </AnimatePresence>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* User Documents Modal */}
      <UserDocumentsModal
        open={showDocumentsModal}
        onClose={() => setShowDocumentsModal(false)}
        onDocumentSelect={handleDocumentSelect}
        mode="select"
      />

      {/* OCR Choice Popup */}
      <Dialog
        open={showOCRChoicePopup}
        onClose={() => setShowOCRChoicePopup(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            📄 Existing Analysis Found
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
            We found existing OCR analysis data for this document. Would you like to use the existing analysis or create a new one?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleUseExistingOCR}
              startIcon={<CheckIcon />}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
              }}
            >
              Use Existing Analysis
            </Button>
            <Button
              variant="outlined"
              onClick={handleCreateNewAnalysis}
              startIcon={<RefreshIcon />}
              sx={{
                borderColor: '#8b5cf6',
                color: '#8b5cf6',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                '&:hover': {
                  borderColor: '#7c3aed',
                  backgroundColor: alpha('#8b5cf6', 0.05),
                },
              }}
            >
              Create New Analysis
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Generated Application Popup */}
      <Dialog
        open={showApplicationPopup}
        onClose={() => setShowApplicationPopup(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 2,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {applicationError ? '❌ Edge Function Error' : '✅ Edge Function Response'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {applicationError ? (
            // Error display
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" sx={{ color: '#ef4444', mb: 2 }}>
                Edge Function Failed
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
                {applicationError}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setShowApplicationPopup(false)}
                sx={{
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  '&:hover': {
                    borderColor: '#dc2626',
                    backgroundColor: alpha('#ef4444', 0.05),
                  },
                }}
              >
                Close
              </Button>
            </Box>
             ) : generatedApplication ? (
               // Beautiful response display
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Success Status */}
              <Paper elevation={1} sx={{ 
                p: 3, 
                borderRadius: 2, 
                backgroundColor: generatedApplication.success ? alpha('#10b981', 0.05) : alpha('#ef4444', 0.05),
                border: `1px solid ${generatedApplication.success ? '#10b981' : '#ef4444'}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: generatedApplication.success ? '#10b981' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {generatedApplication.success ? (
                      <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
                    ) : (
                      <CloseIcon sx={{ color: 'white', fontSize: 20 }} />
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {generatedApplication.success ? '✅ Success!' : '❌ Failed'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Edge function executed successfully
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Message */}
              {generatedApplication.message && (
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    💬 Response Message
                  </Typography>
                  <Box sx={{ 
                    p: 3,
                    backgroundColor: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                  }}>
                    <Typography variant="h5" sx={{ 
                      color: '#1e293b', 
                      fontWeight: 600,
                      textAlign: 'center',
                    }}>
                      {generatedApplication.message}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Timestamp */}
              {generatedApplication.timestamp && (
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    🕒 Execution Time
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#8b5cf6',
                    }} />
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                      {new Date(generatedApplication.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* OCR Data Status */}
              {generatedApplication.ocrDataFound !== undefined && (
                <Paper elevation={1} sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  backgroundColor: generatedApplication.ocrDataFound ? alpha('#10b981', 0.05) : alpha('#f59e0b', 0.05),
                  border: `1px solid ${generatedApplication.ocrDataFound ? '#10b981' : '#f59e0b'}`,
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    📄 OCR Data Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: generatedApplication.ocrDataFound ? '#10b981' : '#f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {generatedApplication.ocrDataFound ? (
                        <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                      ) : (
                        <CloseIcon sx={{ color: 'white', fontSize: 16 }} />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {generatedApplication.ocrDataFound ? 'OCR Data Found!' : 'No OCR Data Found'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {generatedApplication.ocrDataFound 
                          ? `Document has ${generatedApplication.ocrDataSize || 0} characters of OCR data`
                          : 'This document has not been analyzed yet'
                        }
                      </Typography>
                    </Box>
                  </Box>
                  {generatedApplication.documentId && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        Document ID: {generatedApplication.documentId}
                      </Typography>
                    </Box>
                  )}
                  {generatedApplication.ocrDataPreview && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 1 }}>
                        OCR Data Preview:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontFamily: 'monospace' }}>
                        {Array.isArray(generatedApplication.ocrDataPreview) 
                          ? generatedApplication.ocrDataPreview.join(', ')
                          : generatedApplication.ocrDataPreview
                        }
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}

              {/* Job Data Status */}
              {generatedApplication.jobDataFound !== undefined && (
                <Paper elevation={1} sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  backgroundColor: generatedApplication.jobDataFound ? alpha('#3b82f6', 0.05) : alpha('#f59e0b', 0.05),
                  border: `1px solid ${generatedApplication.jobDataFound ? '#3b82f6' : '#f59e0b'}`,
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    💼 Job Data Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: generatedApplication.jobDataFound ? '#3b82f6' : '#f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {generatedApplication.jobDataFound ? (
                        <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                      ) : (
                        <CloseIcon sx={{ color: 'white', fontSize: 16 }} />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {generatedApplication.jobDataFound ? 'Job Data Found!' : 'No Job Data Found'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {generatedApplication.jobDataFound 
                          ? `Job has ${generatedApplication.jobDataSize || 0} characters of data`
                          : 'Job information is not available'
                        }
                      </Typography>
                    </Box>
                  </Box>
                  
                  {generatedApplication.jobId && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        Job ID: {generatedApplication.jobId}
                      </Typography>
                    </Box>
                  )}
                  
                  {generatedApplication.jobDataFound && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {generatedApplication.jobTitle && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Job Title:
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600 }}>
                            {generatedApplication.jobTitle}
                          </Typography>
                        </Box>
                      )}
                      
                      {generatedApplication.jobCompany && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Company:
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b' }}>
                            {generatedApplication.jobCompany}
                          </Typography>
                        </Box>
                      )}
                      
                      {generatedApplication.jobLocation && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Location:
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b' }}>
                            {generatedApplication.jobLocation}
                          </Typography>
                        </Box>
                      )}
                      
                      {generatedApplication.jobDescription && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Description:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1e293b' }}>
                            {generatedApplication.jobDescription}
                          </Typography>
                        </Box>
                      )}
                      
                      {generatedApplication.jobRequirements && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Requirements:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1e293b' }}>
                            {Array.isArray(generatedApplication.jobRequirements) 
                              ? generatedApplication.jobRequirements.slice(0, 3).join(', ') + (generatedApplication.jobRequirements.length > 3 ? '...' : '')
                              : generatedApplication.jobRequirements
                            }
                          </Typography>
                        </Box>
                      )}
                      
                      {(generatedApplication.jobRequiredSkills || generatedApplication.requiredSkills) && Array.isArray(generatedApplication.jobRequiredSkills || generatedApplication.requiredSkills) && (generatedApplication.jobRequiredSkills || generatedApplication.requiredSkills).length > 0 && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 1.5 }}>
                            Required Skills:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(generatedApplication.jobRequiredSkills || generatedApplication.requiredSkills).slice(0, 8).map((skill: string, index: number) => (
                              <Box
                                key={index}
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  borderRadius: 2,
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.025em'
                                }}
                              >
                                {skill}
                              </Box>
                            ))}
                            {(generatedApplication.jobRequiredSkills || generatedApplication.requiredSkills).length > 8 && (
                              <Box
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  backgroundColor: '#e5e7eb',
                                  color: '#6b7280',
                                  borderRadius: 2,
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}
                              >
                                +{(generatedApplication.jobRequiredSkills || generatedApplication.requiredSkills).length - 8} more
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                      
                      {generatedApplication.jobBenefitsPerks && Array.isArray(generatedApplication.jobBenefitsPerks) && generatedApplication.jobBenefitsPerks.length > 0 && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Benefits & Perks:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1e293b' }}>
                            {generatedApplication.jobBenefitsPerks.slice(0, 3).join(', ') + (generatedApplication.jobBenefitsPerks.length > 3 ? '...' : '')}
                          </Typography>
                        </Box>
                      )}
                      
                      {(generatedApplication.jobExperienceLevel || generatedApplication.jobDuration || generatedApplication.jobType || generatedApplication.jobEmploymentType) && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 1 }}>
                            Job Details:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {generatedApplication.jobExperienceLevel && (
                              <Box sx={{ px: 1.5, py: 0.5, backgroundColor: '#e0f2fe', borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 500 }}>
                                  {generatedApplication.jobExperienceLevel}
                                </Typography>
                              </Box>
                            )}
                            {generatedApplication.jobDuration && (
                              <Box sx={{ px: 1.5, py: 0.5, backgroundColor: '#f0fdf4', borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#166534', fontWeight: 500 }}>
                                  {generatedApplication.jobDuration}
                                </Typography>
                              </Box>
                            )}
                            {generatedApplication.jobType && (
                              <Box sx={{ px: 1.5, py: 0.5, backgroundColor: '#fef3c7', borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 500 }}>
                                  {generatedApplication.jobType}
                                </Typography>
                              </Box>
                            )}
                            {generatedApplication.jobEmploymentType && (
                              <Box sx={{ px: 1.5, py: 0.5, backgroundColor: '#f3e8ff', borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 500 }}>
                                  {generatedApplication.jobEmploymentType}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                      
                      {(generatedApplication.jobSalaryMin || generatedApplication.jobSalaryMax) && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Salary:
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600 }}>
                            {generatedApplication.jobSalaryCurrency || 'USD'} {generatedApplication.jobSalaryMin || 'N/A'} - {generatedApplication.jobSalaryMax || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                      
                      {generatedApplication.jobRemoteWorkAllowed !== null && (
                        <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 0.5 }}>
                            Remote Work:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: generatedApplication.jobRemoteWorkAllowed ? '#10b981' : '#ef4444',
                            }} />
                            <Typography variant="body2" sx={{ color: '#1e293b' }}>
                              {generatedApplication.jobRemoteWorkAllowed ? 'Allowed' : 'Not Allowed'}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              )}

              {/* Additional Data */}
              {Object.keys(generatedApplication).filter(key => !['success', 'message', 'timestamp', 'documentId', 'ocrDataFound', 'ocrDataPreview', 'ocrDataSize', 'jobId', 'jobDataFound', 'jobTitle', 'jobCompany', 'jobLocation', 'jobDescription', 'jobRequirements', 'jobRequiredSkills', 'requiredSkills', 'jobBenefitsPerks', 'jobExperienceLevel', 'jobDuration', 'jobRemoteWorkAllowed', 'jobType', 'jobEmploymentType', 'jobSalaryMin', 'jobSalaryMax', 'jobSalaryCurrency', 'jobDataSize', 'generatedApplication', 'applicationError', 'apiKeyFound', 'apiKeyError', 'totalApiKeys', 'usedApiKeyIndex', 'retryInfo'].includes(key)).length > 0 && (
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    📊 Additional Data
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(generatedApplication)
                      .filter(([key]) => !['success', 'message', 'timestamp', 'documentId', 'ocrDataFound', 'ocrDataPreview', 'ocrDataSize', 'jobId', 'jobDataFound', 'jobTitle', 'jobCompany', 'jobLocation', 'jobDescription', 'jobRequirements', 'jobRequiredSkills', 'requiredSkills', 'jobBenefitsPerks', 'jobExperienceLevel', 'jobDuration', 'jobRemoteWorkAllowed', 'jobType', 'jobEmploymentType', 'jobSalaryMin', 'jobSalaryMax', 'jobSalaryCurrency', 'jobDataSize', 'generatedApplication', 'applicationError', 'apiKeyFound', 'apiKeyError', 'totalApiKeys', 'usedApiKeyIndex', 'retryInfo'].includes(key))
                      .map(([key, value]) => (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                            {key}:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1e293b' }}>
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Paper>
              )}

              {/* Generated Application Letter */}
              {generatedApplication.generatedApplication && (
                <Paper elevation={3} sx={{ 
                  borderRadius: 4, 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}>
             {/* Header */}
             <Box sx={{ 
               backgroundColor: 'white',
               p: 3,
               borderBottom: '1px solid #e2e8f0',
               position: 'relative'
             }}>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                   <Box sx={{
                     width: 48,
                     height: 48,
                     borderRadius: '50%',
                     backgroundColor: '#f8fafc',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     border: '2px solid #e2e8f0'
                   }}>
                     <Typography sx={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '1.4rem' }}>
                       ✉️
                     </Typography>
                   </Box>
                   <Box>
                     <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                       Generated Application
                     </Typography>
                     <Typography variant="body1" sx={{ color: '#64748b', fontSize: '1rem' }}>
                       AI-powered application letter tailored to this position
                     </Typography>
                   </Box>
                 </Box>
                 
                 {/* Action Buttons */}
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <IconButton
                     size="small"
                     sx={{
                       color: '#64748b',
                       '&:hover': {
                         backgroundColor: '#f1f5f9',
                         color: '#3b82f6'
                       }
                     }}
                   >
                     <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                       <Box sx={{
                         width: 12,
                         height: 12,
                         border: '1.5px solid currentColor',
                         borderRadius: '2px'
                       }} />
                     </Box>
                   </IconButton>
                   <IconButton
                     size="small"
                     onClick={() => setShowApplicationPopup(false)}
                     sx={{
                       color: '#64748b',
                       '&:hover': {
                         backgroundColor: '#fef2f2',
                         color: '#dc2626'
                       }
                     }}
                   >
                     <CloseIcon sx={{ fontSize: 18 }} />
                   </IconButton>
                 </Box>
               </Box>
             </Box>
                  
                  {/* Application Content */}
                  <Box sx={{
                    backgroundColor: '#fefefe',
                    border: '1px solid #f1f5f9',
                    borderRadius: 0,
                    p: 4,
                    maxHeight: '500px',
                    overflowY: 'auto',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, #e2e8f0, transparent)'
                    },
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f8fafc',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#cbd5e1',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#94a3b8',
                      },
                    },
                  }}>
                    <Box
                      sx={{ 
                        color: '#1e293b',
                        lineHeight: 1.8,
                        whiteSpace: 'pre-wrap',
                        fontFamily: '"Times New Roman", Georgia, serif',
                        fontSize: '1rem',
                        textAlign: 'justify',
                        '& strong': {
                          fontWeight: 700,
                          color: '#1e293b'
                        },
                        '& em': {
                          fontStyle: 'italic',
                          color: '#4b5563'
                        }
                      }}
                      dangerouslySetInnerHTML={{
                        __html: generatedApplication.generatedApplication
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
                          .replace(/\n/g, '<br/>')
                      }}
                    />
                  </Box>
                  
                  {/* Footer */}
                  <Box sx={{ 
                    backgroundColor: '#f8fafc',
                    p: 2,
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#10b981'
                        }} />
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                          Generated by AI
                        </Typography>
                      </Box>
                      {generatedApplication.savedApplicationId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#3b82f6'
                          }} />
                          <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 500 }}>
                            Saved to Database
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                      {new Date().toLocaleString()}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Save Status */}
              {generatedApplication.saveError && (
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '0.8rem' }}>✗</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626' }}>
                      Database Save Failed
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#dc2626' }}>
                    {generatedApplication.saveError}
                  </Typography>
                </Paper>
              )}

              {/* API Key Status */}
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: generatedApplication.apiKeyFound ? '#f0fdf4' : '#fef2f2', border: `1px solid ${generatedApplication.apiKeyFound ? '#bbf7d0' : '#fecaca'}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: generatedApplication.apiKeyFound ? '#10b981' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {generatedApplication.apiKeyFound ? (
                      <Typography sx={{ color: 'white', fontSize: '0.8rem' }}>✓</Typography>
                    ) : (
                      <Typography sx={{ color: 'white', fontSize: '0.8rem' }}>✗</Typography>
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: generatedApplication.apiKeyFound ? '#166534' : '#dc2626' }}>
                    API Key Status
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: generatedApplication.apiKeyFound ? '#166534' : '#dc2626' }}>
                  {generatedApplication.apiKeyFound 
                    ? `Found ${generatedApplication.totalApiKeys || 1} Mistral API key${(generatedApplication.totalApiKeys || 1) > 1 ? 's' : ''} and ready to use` 
                    : generatedApplication.apiKeyError || 'No Mistral API key found'
                  }
                </Typography>
                {generatedApplication.retryInfo && (
                  <Typography variant="body2" sx={{ color: '#166534', mt: 1, fontStyle: 'italic' }}>
                    {generatedApplication.retryInfo}
                  </Typography>
                )}
              </Paper>

              {/* Application Error */}
              {generatedApplication.applicationError && (
                <Paper elevation={2} sx={{ p: 4, borderRadius: 3, backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.2rem' }}>
                        ⚠️
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626', mb: 0.5 }}>
                        Application Generation Failed
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#991b1b' }}>
                        There was an error generating the job application
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{
                    backgroundColor: 'white',
                    border: '1px solid #fecaca',
                    borderRadius: 2,
                    p: 3,
                    mt: 2
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#dc2626',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {generatedApplication.applicationError}
                    </Typography>
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setShowApplicationPopup(false)}
                  startIcon={<CheckIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    },
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      <DialogActions sx={{ 
        p: 3, 
        pt: 1, 
        justifyContent: 'space-between',
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'white',
        borderTop: '1px solid #e2e8f0',
        zIndex: 1
      }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{
                color: '#64748b',
                borderColor: '#e2e8f0',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Back
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleClose}
            sx={{
              color: '#64748b',
              borderColor: '#e2e8f0',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          
          {!(activeStep === 4 && !isLoadingJobDetails && !currentJobDetails && currentJobIndex >= selectedJobsData.length) ? (
            <Button
              onClick={handleNext}
              variant="contained"
              endIcon={activeStep === 1 && uploadingOCR ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
               disabled={
                 (activeStep === 0 && (!selectedCV || processingCV)) ||
                 (activeStep === 1 && uploadingOCR) || // Disable Next button while uploading OCR
                 (activeStep === 2 && selectedJobsData.length === 0) ||
                 (activeStep === 3) || // Disable Next button in AI Generation step
                 (activeStep === 4 && !(currentJobIndex >= selectedJobsData.length)) // Disable Next button in Generation step until finish card
               }
              sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                },
                '&:disabled': {
                  backgroundColor: '#e2e8f0',
                  color: '#94a3b8',
                },
              }}
             >
               {activeStep === 0 && processingCV ? 'Processing...' : 
                activeStep === 1 && uploadingOCR ? 'Uploading...' : 'Next'}
             </Button>
           ) : (
             <Button
               onClick={handleClose}
               variant="contained"
               startIcon={<CheckIcon />}
               sx={{
                 background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                 borderRadius: 2,
                 textTransform: 'none',
                 fontWeight: 600,
                 px: 3,
                 '&:hover': {
                   background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                 },
               }}
             >
               Finish
             </Button>
           )}
        </Box>
      </DialogActions>
      </motion.div>
    </Dialog>

    {/* Generated Application Modal */}
    <Dialog
      open={showApplicationModal}
      onClose={() => setShowApplicationModal(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid #e2e8f0',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon sx={{ color: '#10b981' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Generated Application
          </Typography>
        </Box>
        <IconButton onClick={() => setShowApplicationModal(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {selectedApplication ? (
          <Box>
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                {selectedApplication.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Status: <Chip 
                  label={selectedApplication.status} 
                  size="small" 
                  sx={{ 
                    backgroundColor: selectedApplication.status === 'draft' ? '#fef3c7' : '#d1fae5',
                    color: selectedApplication.status === 'draft' ? '#d97706' : '#059669',
                    ml: 1
                  }} 
                />
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                Created: {new Date(selectedApplication.created_at).toLocaleDateString()}
              </Typography>
            </Box>
            
            <Box sx={{ 
              p: 3, 
              backgroundColor: '#ffffff', 
              borderRadius: 2, 
              border: '1px solid #e2e8f0',
              minHeight: '400px'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                Cover Letter
              </Typography>
              <Box sx={{ 
                lineHeight: 1.6, 
                color: '#374151',
                fontSize: '0.9rem',
                '& strong': {
                  fontWeight: 600,
                  color: '#1e293b'
                },
                '& p': {
                  marginBottom: '1rem'
                }
              }}>
                <ReactMarkdown>{selectedApplication.letter}</ReactMarkdown>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2, color: '#64748b' }}>
              Loading application...
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
        <Button 
          onClick={() => setShowApplicationModal(false)}
          variant="outlined"
          sx={{ 
            borderColor: '#64748b',
            color: '#64748b',
            '&:hover': {
              borderColor: '#3b82f6',
              color: '#3b82f6',
            }
          }}
        >
          Close
        </Button>
        <Button 
          variant="contained"
          onClick={() => {
            if (selectedApplication?.letter) {
              navigator.clipboard.writeText(selectedApplication.letter);
              // You could add a toast notification here
            }
          }}
          sx={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            }
          }}
        >
          Copy to Clipboard
        </Button>
      </DialogActions>
    </Dialog>

    {/* Replace Application Confirmation Modal */}
    <Dialog
      open={showReplaceConfirmModal}
      onClose={() => setShowReplaceConfirmModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid #e2e8f0',
        pb: 2
      }}>
        <ErrorIcon sx={{ color: '#f59e0b' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Replace Existing Application?
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2 }}>
          You already have a generated application for this job:
        </Typography>
        
        {jobToReplace && (
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#f8fafc', 
            borderRadius: 2, 
            border: '1px solid #e2e8f0',
            mb: 2
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
              {jobToReplace.title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {jobToReplace.company} • {jobToReplace.location}
            </Typography>
          </Box>
        )}
        
        <Typography variant="body1" sx={{ color: '#374151' }}>
          Generating a new application will replace the existing one. This action cannot be undone.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
        <Button 
          onClick={() => setShowReplaceConfirmModal(false)}
          variant="outlined"
          sx={{ 
            borderColor: '#64748b',
            color: '#64748b',
            '&:hover': {
              borderColor: '#3b82f6',
              color: '#3b82f6',
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained"
          onClick={handleReplaceApplication}
          sx={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
            }
          }}
        >
          Replace Application
        </Button>
      </DialogActions>
    </Dialog>

    </>
  );
};

export default AIApplicationWriterModal;
